import React, { FC, useState, ReactNode } from 'react';
import { addSeconds } from 'date-fns';
import cns from 'classnames';

import { MAX_ATTEMPTS_CODE_REACHED } from 'constants/errors';
import Button from 'components/Button/Button';
import CodeInput from 'components/CodeInput/CodeInput';
import { ResendCode } from 'components/ResendCode/ResendCode';
import Checkbox from 'components/form/Checkbox/Checkbox';
import styles from './CodeConfirmation.module.scss';

interface Props {
  onSubmit: (code: string) => Promise<any>;
  onSmsResend: () => Promise<any>;
  checkbox?: ReactNode;
  appearance?: 'default' | 'login';
}

const DURATION = 45;
const LENGTH = 4;

export const CodeConfirmation: FC<Props> = ({ onSubmit, onSmsResend, checkbox, appearance = 'default' }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendAvailableDT, setResendAvailableDT] = useState(addSeconds(new Date(), DURATION));
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const handleSubmit = async () => {
    if (submitting || code.length !== LENGTH || error || (!!checkbox && !checkboxChecked)) return;
    try {
      setSubmitting(true);
      await onSubmit(code);
    } catch (e: any) {
      if (e?.data?.message === 'need_new_code') {
        setError(MAX_ATTEMPTS_CODE_REACHED);
      } else {
        setError('Неверный код');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (value: string) => {
    if (error) {
      setError('');
    }
    setCode(value);
  };

  const handleSmsResend = async () => {
    await onSmsResend();
    setResendAvailableDT(addSeconds(new Date(), DURATION));
    setError('');
  };

  return (
    <div className={cns(styles.wrapper, { [styles.login]: appearance === 'login' })}>
      <div className={styles.title}>Код из СМС</div>
      <div className={styles.codeFields}>
        <CodeInput length={LENGTH} onChange={handleChange} value={code} name="code" autoFocus />
      </div>
      {!!checkbox && <Checkbox
        className={styles.checkbox}
        checked={checkboxChecked}
        onChange={setCheckboxChecked}
      >{checkbox}</Checkbox>}
      <div className={styles.error} role="status">{error}</div>
      <div className={styles.footer}>
        <Button
          className={styles.btn}
          disabled={submitting || code?.length !== LENGTH || !!error || (!!checkbox && !checkboxChecked)}
          onClick={handleSubmit}
        >Подтвердить</Button>
        <ResendCode onSubmit={handleSmsResend} endTime={resendAvailableDT} />
      </div>
    </div>
  );
};
